/**
 * Vault Management page
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Common/Header";
import LoadingSpinner from "../components/Common/LoadingSpinner";
import AddMemberModal from "../components/Vault/AddMemberModal";
import InvitationsTable from "../components/Vault/InvitationsTable";
import { useVaultContext } from "../context/VaultContext";
import {
  useRemoveVaultMember,
  useUpdateVault,
  useUpdateVaultMember,
  useVaultMembers,
} from "../hooks/useVault";

type Tab = "members" | "invitations";

export default function VaultManagement() {
  const navigate = useNavigate();
  const { currentVault, currentVaultId } = useVaultContext();
  const [activeTab, setActiveTab] = useState<Tab>("members");
  const [showAddMember, setShowAddMember] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [editingDescription, setEditingDescription] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  const { data: members = [], isLoading: membersLoading } = useVaultMembers(currentVaultId);
  const updateVaultMutation = useUpdateVault(currentVaultId || 0);
  const updateMemberMutation = useUpdateVaultMember(currentVaultId || 0);
  const removeMemberMutation = useRemoveVaultMember(currentVaultId || 0);

  if (!currentVaultId || !currentVault) {
    return (
      <div className="min-h-screen bg-vault-50">
        <Header />
        <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
          <div className="rounded-lg border border-vault-200 bg-white p-8 text-center">
            <p className="text-vault-600">No vault selected</p>
            <button
              onClick={() => navigate("/dashboard")}
              className="mt-4 text-vault-600 hover:text-vault-900 underline"
            >
              Return to Dashboard
            </button>
          </div>
        </main>
      </div>
    );
  }

  const handleSaveName = async () => {
    if (name.trim()) {
      await updateVaultMutation.mutateAsync({ name: name.trim() });
      setEditingName(false);
    }
  };

  const handleSaveDescription = async () => {
    await updateVaultMutation.mutateAsync({
      description: description.trim() || undefined,
    });
    setEditingDescription(false);
  };

  const handleChangeRole = async (userId: string, newRole: "VAULT_CO_ADMIN" | "VAULT_MEMBER") => {
    if (confirm("Are you sure you want to change this member's role?")) {
      await updateMemberMutation.mutateAsync({ userId, data: { role: newRole } });
    }
  };

  const handleRemoveMember = async (userId: string, email: string | null) => {
    if (confirm(`Are you sure you want to remove ${email || 'this user'} from this vault?`)) {
      await removeMemberMutation.mutateAsync(userId);
    }
  };

  const isOwnerOrAdmin = members.some(
    (m) =>
      m.user_id === currentVault.primary_owner_user_id &&
      (m.role === "VAULT_OWNER" || m.role === "VAULT_CO_ADMIN")
  );

  return (
    <div className="min-h-screen bg-vault-50">
      <Header />

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="font-serif text-3xl font-bold text-vault-900">
              Vault Management
            </h1>
            <p className="mt-2 text-vault-600">
              Manage vault settings and member access
            </p>
          </div>
          <button
            onClick={() => navigate("/dashboard")}
            className="text-vault-600 hover:text-vault-900 flex items-center gap-2"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="h-5 w-5"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18"
              />
            </svg>
            Back to Dashboard
          </button>
        </div>

        <div className="space-y-6">
          {/* Vault Details Card */}
          <div className="rounded-lg border border-vault-200 bg-white p-6">
            <h2 className="font-serif text-xl font-semibold text-vault-900 mb-4">
              Vault Details
            </h2>

            {/* Name */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-vault-700 mb-1">
                Name
              </label>
              {editingName ? (
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="flex-1 rounded-md border border-vault-300 px-3 py-2 text-sm focus:border-vault-500 focus:outline-none focus:ring-1 focus:ring-vault-500"
                    autoFocus
                  />
                  <button
                    onClick={handleSaveName}
                    disabled={updateVaultMutation.isPending}
                    className="rounded-md bg-vault-600 px-4 py-2 text-sm font-medium text-white hover:bg-vault-700 disabled:opacity-50"
                  >
                    Save
                  </button>
                  <button
                    onClick={() => {
                      setEditingName(false);
                      setName(currentVault.name);
                    }}
                    className="rounded-md border border-vault-300 px-4 py-2 text-sm font-medium text-vault-700 hover:bg-vault-50"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <span className="text-vault-900">{currentVault.name}</span>
                  {isOwnerOrAdmin && (
                    <button
                      onClick={() => {
                        setName(currentVault.name);
                        setEditingName(true);
                      }}
                      className="text-sm text-vault-600 hover:text-vault-900"
                    >
                      Edit
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Description */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-vault-700 mb-1">
                Description
              </label>
              {editingDescription ? (
                <div className="space-y-2">
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={3}
                    className="w-full rounded-md border border-vault-300 px-3 py-2 text-sm focus:border-vault-500 focus:outline-none focus:ring-1 focus:ring-vault-500"
                    autoFocus
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={handleSaveDescription}
                      disabled={updateVaultMutation.isPending}
                      className="rounded-md bg-vault-600 px-4 py-2 text-sm font-medium text-white hover:bg-vault-700 disabled:opacity-50"
                    >
                      Save
                    </button>
                    <button
                      onClick={() => {
                        setEditingDescription(false);
                        setDescription(currentVault.description || "");
                      }}
                      className="rounded-md border border-vault-300 px-4 py-2 text-sm font-medium text-vault-700 hover:bg-vault-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-start justify-between">
                  <span className="text-vault-600">
                    {currentVault.description || (
                      <em className="text-vault-400">No description</em>
                    )}
                  </span>
                  {isOwnerOrAdmin && (
                    <button
                      onClick={() => {
                        setDescription(currentVault.description || "");
                        setEditingDescription(true);
                      }}
                      className="text-sm text-vault-600 hover:text-vault-900"
                    >
                      Edit
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Metadata */}
            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-vault-200 text-sm">
              <div>
                <span className="text-vault-500">Created:</span>
                <span className="ml-2 text-vault-900">
                  {new Date(currentVault.created_at).toLocaleDateString()}
                </span>
              </div>
              <div>
                <span className="text-vault-500">Updated:</span>
                <span className="ml-2 text-vault-900">
                  {new Date(currentVault.updated_at).toLocaleDateString()}
                </span>
              </div>
            </div>
          </div>

          {/* Tabs Navigation */}
          <div className="rounded-lg border border-vault-200 bg-white">
            <div className="border-b border-vault-200">
              <nav className="flex gap-4 px-6" aria-label="Tabs">
                <button
                  onClick={() => setActiveTab("members")}
                  className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === "members"
                      ? "border-vault-600 text-vault-600"
                      : "border-transparent text-vault-500 hover:text-vault-700 hover:border-vault-300"
                  }`}
                >
                  Members
                </button>
                <button
                  onClick={() => setActiveTab("invitations")}
                  className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === "invitations"
                      ? "border-vault-600 text-vault-600"
                      : "border-transparent text-vault-500 hover:text-vault-700 hover:border-vault-300"
                  }`}
                >
                  Pending Invitations
                </button>
              </nav>
            </div>

            <div className="p-6">
              {/* Members Tab */}
              {activeTab === "members" && (
                <>
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="font-serif text-xl font-semibold text-vault-900">
                      Vault Members
                    </h2>
                    {isOwnerOrAdmin && (
                      <button
                        onClick={() => setShowAddMember(true)}
                        className="rounded-md bg-vault-600 px-4 py-2 text-sm font-medium text-white hover:bg-vault-700 transition-colors flex items-center gap-2"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className="h-4 w-4"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M12 4.5v15m7.5-7.5h-15"
                          />
                        </svg>
                        Invite Member
                      </button>
                    )}
                  </div>

            {membersLoading ? (
              <div className="flex justify-center py-8">
                <LoadingSpinner size="md" />
              </div>
            ) : members.length === 0 ? (
              <p className="text-center text-vault-500 py-8">No members found</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-vault-200 text-left text-sm text-vault-600">
                      <th className="pb-3 font-medium">Email</th>
                      <th className="pb-3 font-medium">Name</th>
                      <th className="pb-3 font-medium">Role</th>
                      <th className="pb-3 font-medium">Added</th>
                      {isOwnerOrAdmin && <th className="pb-3 font-medium">Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {members.map((member, idx) => (
                      <tr
                        key={member.user_id}
                        className={`text-sm ${
                          idx % 2 === 0 ? "bg-vault-50" : "bg-white"
                        } hover:bg-vault-100 transition-colors`}
                      >
                        <td className="py-3 px-2">
                          {member.user_email || <em className="text-vault-400">Not set</em>}
                        </td>
                        <td className="py-3 px-2">
                          {member.user_name || <em className="text-vault-400">Not set</em>}
                        </td>
                        <td className="py-3 px-2">
                          <span
                            className={`inline-block rounded-full px-2 py-1 text-xs font-medium ${
                              member.role === "VAULT_OWNER"
                                ? "bg-purple-100 text-purple-800"
                                : member.role === "VAULT_CO_ADMIN"
                                ? "bg-blue-100 text-blue-800"
                                : "bg-green-100 text-green-800"
                            }`}
                          >
                            {member.role === "VAULT_OWNER"
                              ? "Owner"
                              : member.role === "VAULT_CO_ADMIN"
                              ? "Co-Admin"
                              : "Member"}
                          </span>
                        </td>
                        <td className="py-3 px-2 text-vault-600">
                          {new Date(member.created_at).toLocaleDateString()}
                        </td>
                        {isOwnerOrAdmin && (
                          <td className="py-3 px-2">
                            {member.role !== "VAULT_OWNER" && (
                              <div className="flex gap-2">
                                <select
                                  value={member.role}
                                  onChange={(e) =>
                                    handleChangeRole(
                                      member.user_id,
                                      e.target.value as "VAULT_CO_ADMIN" | "VAULT_MEMBER"
                                    )
                                  }
                                  className="text-xs rounded border border-vault-300 px-2 py-1 focus:border-vault-500 focus:outline-none"
                                  disabled={updateMemberMutation.isPending}
                                >
                                  <option value="VAULT_MEMBER">Member</option>
                                  <option value="VAULT_CO_ADMIN">Co-Admin</option>
                                </select>
                                <button
                                  onClick={() =>
                                    handleRemoveMember(member.user_id, member.user_email)
                                  }
                                  disabled={removeMemberMutation.isPending}
                                  className="text-red-600 hover:text-red-800 disabled:opacity-50"
                                  title="Remove member"
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    strokeWidth={1.5}
                                    stroke="currentColor"
                                    className="h-4 w-4"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
                                    />
                                  </svg>
                                </button>
                              </div>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
                </>
              )}

              {/* Invitations Tab */}
              {activeTab === "invitations" && (
                <>
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="font-serif text-xl font-semibold text-vault-900">
                      Pending Invitations
                    </h2>
                    {isOwnerOrAdmin && (
                      <button
                        onClick={() => setShowAddMember(true)}
                        className="rounded-md bg-vault-600 px-4 py-2 text-sm font-medium text-white hover:bg-vault-700 transition-colors flex items-center gap-2"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className="h-4 w-4"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M12 4.5v15m7.5-7.5h-15"
                          />
                        </svg>
                        Create Invitation
                      </button>
                    )}
                  </div>
                  <InvitationsTable vaultId={currentVaultId} />
                </>
              )}
            </div>
          </div>
        </div>
      </main>

      <AddMemberModal
        vaultId={currentVaultId}
        isOpen={showAddMember}
        onClose={() => setShowAddMember(false)}
      />
    </div>
  );
}
